import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ApiHelper } from '../../../ApiHelper';
import {
    AgencyModelsSettingResult,
    AgencyJobCreationStatus,
    AgencyJobCreationThrottleType,
    AgencyModelPickerItem,
    AgentTaskEnqueueSubmissionMode,
    JobDefinitionSummary,
    JobModelConfiguration,
    JobDefinitionCollectionResult,
    EnqueueAgentTasksResult,
    EnqueueAgentTasksRequest,
    EnqueueAgentTaskPromptPreviewResult,
    AgentTaskListDetailResult,
    AgentTaskSnapshotSummary,
    formatCompactDate,
    formatDate,
    getSnapshotAgeInDays,
    normalizeJobModelConfiguration,
    parseAgencyModelsSettingResponse,
} from '../shared';
import { AgencyModelConfigurationEditor } from '../AgencyModelConfigurationEditor';

export function EnqueueTasksPanel(props: {
    isOpen: boolean;
    serviceName: string;
    taskList?: AgentTaskListDetailResult | null;
    snapshot?: AgentTaskSnapshotSummary | null;
    taskIds: number[];
    selectedProposalIds?: number[];
    onDismiss: () => void;
    onEnqueued: (result: EnqueueAgentTasksResult) => void;
}) {
    const {
        isOpen,
        serviceName,
        taskList,
        snapshot,
        taskIds,
        selectedProposalIds = [],
        onDismiss,
        onEnqueued,
    } = props;
    const [availableJobs, setAvailableJobs] = React.useState<JobDefinitionSummary[]>([]);
    const [selectedJobName, setSelectedJobName] = React.useState<string | undefined>(undefined);
    const [loadingAgents, setLoadingAgents] = React.useState(false);
    const [agencyModelOptions, setAgencyModelOptions] = React.useState<AgencyModelPickerItem[]>([]);
    const [agencyModelsLoading, setAgencyModelsLoading] = React.useState(false);
    const [agencyModelsMessage, setAgencyModelsMessage] = React.useState<string | null>(null);
    const [overrideModelConfiguration, setOverrideModelConfiguration] = React.useState<JobModelConfiguration | null>(null);
    const [submitDirectlyToAgency, setSubmitDirectlyToAgency] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);
    const [submitNotice, setSubmitNotice] = React.useState<string | null>(null);
    const [submissionCompleted, setSubmissionCompleted] = React.useState(false);
    const [partialFailureAfterSubmit, setPartialFailureAfterSubmit] = React.useState(false);
    const [agencyJobCreationStatus, setAgencyJobCreationStatus] = React.useState<AgencyJobCreationStatus | null>(null);
    const [agencyStatusError, setAgencyStatusError] = React.useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [promptPreview, setPromptPreview] = React.useState<EnqueueAgentTaskPromptPreviewResult | null>(null);
    const taskIdsKey = taskIds.join(',');
    const selectedProposalIdsKey = selectedProposalIds.join(',');
    const isPromptPreviewOpen = promptPreview !== null;

    React.useEffect(() => {
        if (!isOpen) {
            setAvailableJobs([]);
            setSelectedJobName(undefined);
            setAgencyModelOptions([]);
            setAgencyModelsLoading(false);
            setAgencyModelsMessage(null);
            setOverrideModelConfiguration(null);
            setSubmitDirectlyToAgency(false);
            setPartialFailureAfterSubmit(false);
            setSubmitNotice(null);
            setSubmissionCompleted(false);
            setAgencyJobCreationStatus(null);
            setAgencyStatusError(null);
            setPreviewLoading(false);
            setPromptPreview(null);
            return;
        }

        setSubmitError(null);
        setSubmitNotice(null);
        setSubmissionCompleted(false);
        setPartialFailureAfterSubmit(false);
    }, [isOpen]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        let isMounted = true;
        ApiHelper(`/api/service/${serviceName}/agencyjobcreationstatus`)
            .then((status: AgencyJobCreationStatus) => {
                if (isMounted) {
                    setAgencyJobCreationStatus(status.isPaused ? status : null);
                    setAgencyStatusError(null);
                }
            })
            .catch((err: Error) => {
                if (isMounted) {
                    setAgencyStatusError(err.message || 'Failed to load Agency job creation status.');
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isOpen, serviceName]);

    React.useEffect(() => {
        setPromptPreview(null);
    }, [snapshot?.snapshotId, taskIdsKey, selectedProposalIdsKey, overrideModelConfiguration]);

    React.useEffect(() => {
        if (!isOpen || !taskList) {
            return;
        }

        let isMounted = true;
        setLoadingAgents(true);
        setSubmitError(null);

        ApiHelper(`/api/service/${serviceName}/jobdefinitions?taskType=${taskList.taskList.taskType}`)
            .then((response: JobDefinitionCollectionResult) => {
                if (!isMounted) {
                    return;
                }

                const jobs = (response.jobDefinitions ?? [])
                    .slice()
                    .sort((left, right) => left.name.localeCompare(right.name));

                setAvailableJobs(jobs);
                setSelectedJobName(jobs[0]?.name);
                setLoadingAgents(false);
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setAvailableJobs([]);
                setSelectedJobName(undefined);
                setSubmitError(err.message || 'Failed to load job definitions.');
                setLoadingAgents(false);
            });

        return () => {
            isMounted = false;
        };
    }, [isOpen, serviceName, taskList]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        let isMounted = true;
        setAgencyModelsLoading(true);
        setAgencyModelsMessage(null);

        ApiHelper(`/api/service/${serviceName}/agencymodels`)
            .then((response: AgencyModelsSettingResult) => {
                if (!isMounted) {
                    return;
                }

                try {
                    const parsedModels = parseAgencyModelsSettingResponse(response);
                    setAgencyModelOptions(parsedModels);
                    if (!response.found || !response.value?.trim()) {
                        setAgencyModelsMessage('No Agency models are configured. Default will use the job definition setting.');
                    } else if (parsedModels.length === 0) {
                        setAgencyModelsMessage('AgencyModels is configured, but it does not contain any models with modelPickerEnabled set to true.');
                    } else {
                        setAgencyModelsMessage(null);
                    }
                } catch (err) {
                    setAgencyModelOptions([]);
                    setAgencyModelsMessage(err instanceof Error ? `Failed to parse AgencyModels: ${err.message}` : 'Failed to parse AgencyModels.');
                } finally {
                    setAgencyModelsLoading(false);
                }
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setAgencyModelOptions([]);
                setAgencyModelsMessage(err.message || 'Failed to load Agency models.');
                setAgencyModelsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [isOpen, serviceName]);

    const availableJobOptions = React.useMemo<IDropdownOption[]>(
        () => availableJobs.map(job => ({
            key: job.name,
            text: job.name,
        })),
        [availableJobs],
    );

    const selectedJob = React.useMemo(
        () => availableJobs.find(job => job.name === selectedJobName),
        [availableJobs, selectedJobName],
    );

    const supportsModelOverride = selectedJob?.executionBackend === 0;
    const isAgencyJob = selectedJob?.executionBackend === 0;

    const snapshotAgeDays = getSnapshotAgeInDays(snapshot);
    const isStaleSnapshot = snapshotAgeDays !== undefined && snapshotAgeDays > 14;

    function buildFailureMessage(result: EnqueueAgentTasksResult) {
        const submittedCount = result.workItems.length;
        const rejectedCount = result.rejectedTaskCount ?? 0;
        const selectedCount = taskIds.length;
        const taskFailureMessages = (result.failures ?? [])
            .map(failure => failure.message.trim())
            .filter(Boolean);
        const reason = taskFailureMessages.length > 0
            ? taskFailureMessages.join('\n')
            : result.failureMessage?.trim() || 'The batch stopped before all selected tasks were submitted.';
        const details: string[] = [];

        if (result.failureStatusCode !== undefined && result.failureStatusCode !== null) {
            details.push(`Response status code: ${result.failureStatusCode}`);
        }

        if (result.failureResponseBody?.trim()) {
            details.push(`Response body:\n${result.failureResponseBody.trim()}`);
        }

        const detailText = details.length > 0
            ? `\n\n${details.join('\n\n')}`
            : '';
        const rejectedText = rejectedCount > 0
            ? ` ${rejectedCount} task${rejectedCount === 1 ? ' was' : 's were'} rejected because no applicable Ok proposal could be submitted.`
            : '';

        if (submittedCount > 0) {
            const failedTask = result.failedTaskId !== undefined && result.failedTaskId !== null
                ? ` Task ${result.failedTaskId} was the first failure.`
                : '';
            const outcome = result.stoppedOnFailure ? 'before the batch stopped' : 'before processing failures';
            return `Submitted ${submittedCount} of ${selectedCount} selected tasks ${outcome}.${rejectedText}${failedTask} ${reason}`.trim() + detailText;
        }

        return `${rejectedText.trim()} ${reason}`.trim() + detailText;
    }

    function buildRejectedTaskMessage(result: EnqueueAgentTasksResult) {
        const submittedCount = result.workItems.length;
        const rejectedCount = result.rejectedTaskCount ?? 0;
        const reasons = (result.failures ?? [])
            .map(failure => failure.message.trim())
            .filter(Boolean);
        const reasonText = reasons.length > 0
            ? ` ${reasons.join(' ')}`
            : '';
        return `Submitted ${submittedCount} task${submittedCount === 1 ? '' : 's'}; `
            + `${rejectedCount} task${rejectedCount === 1 ? ' was' : 's were'} rejected because no applicable Ok proposal could be submitted.`
            + reasonText;
    }

    async function handleSubmit() {
        setSubmitting(true);
        setSubmitError(null);

        try {
            if (!taskList) {
                throw new Error('The task list has not finished loading yet.');
            }

            if (!snapshot) {
                throw new Error('Select a snapshot before enqueueing tasks.');
            }

            if (!selectedJobName) {
                throw new Error('Select a job before enqueueing tasks.');
            }

            const request: EnqueueAgentTasksRequest = {
                taskIds,
                jobName: selectedJobName,
                snapshotId: snapshot.snapshotId,
                modelConfiguration: supportsModelOverride ? normalizeJobModelConfiguration(overrideModelConfiguration) : null,
                submissionMode: isAgencyJob
                    ? (submitDirectlyToAgency
                        ? AgentTaskEnqueueSubmissionMode.SubmitImmediately
                        : AgentTaskEnqueueSubmissionMode.QueueInternally)
                    : AgentTaskEnqueueSubmissionMode.SubmitImmediately,
                selectedProposalIds,
            };
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskList.taskList.taskListId}/enqueue`,
                'POST',
                request,
            ) as EnqueueAgentTasksResult;
            setAgencyJobCreationStatus(result.agencyJobCreationStatus?.isPaused
                ? result.agencyJobCreationStatus
                : null);

            if (result.workItems.length > 0) {
                onEnqueued(result);
            }

            const failureCount = result.failures?.length ?? 0;
            const rejectedCount = result.rejectedTaskCount ?? 0;
            if (!result.stoppedOnFailure && rejectedCount > 0 && failureCount <= rejectedCount) {
                setSubmitNotice(buildRejectedTaskMessage(result));
                setSubmissionCompleted(true);
                return;
            }

            if (result.stoppedOnFailure || failureCount > 0) {
                setPartialFailureAfterSubmit(result.workItems.length > 0);
                setSubmitError(buildFailureMessage(result));
                return;
            }

            if (rejectedCount > 0) {
                setSubmitNotice(buildRejectedTaskMessage(result));
                setSubmissionCompleted(true);
                return;
            }

            onDismiss();
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to enqueue tasks.');
        } finally {
            setSubmitting(false);
        }
    }

    async function handlePreview() {
        setPreviewLoading(true);
        setSubmitError(null);

        try {
            if (!taskList) {
                throw new Error('The task list has not finished loading yet.');
            }
            if (!snapshot) {
                throw new Error('Select a snapshot before previewing the prompt.');
            }
            if (!selectedJobName) {
                throw new Error('Select a job before previewing the prompt.');
            }
            if (taskIds.length === 0) {
                throw new Error('Select at least one task before previewing the prompt.');
            }

            const request: EnqueueAgentTasksRequest = {
                taskIds,
                jobName: selectedJobName,
                snapshotId: snapshot.snapshotId,
                modelConfiguration: supportsModelOverride ? normalizeJobModelConfiguration(overrideModelConfiguration) : null,
                selectedProposalIds,
            };
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskList.taskList.taskListId}/enqueue:preview`,
                'POST',
                request,
            ) as EnqueueAgentTaskPromptPreviewResult;
            setPromptPreview(result);
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to preview the prompt.');
        } finally {
            setPreviewLoading(false);
        }
    }

    function dismissPromptPreview(ev?: React.SyntheticEvent<unknown>) {
        ev?.preventDefault();
        ev?.stopPropagation();
        setPromptPreview(null);
    }

    function handlePanelDismiss() {
        setPromptPreview(null);
        onDismiss();
    }

    return (
        <>
            <Panel
                isOpen={isOpen}
                onDismiss={handlePanelDismiss}
                isBlocking={!isPromptPreviewOpen}
                popupProps={isPromptPreviewOpen
                    ? {
                        onDismiss: ev => {
                            ev.preventDefault();
                            ev.stopPropagation();
                        },
                    }
                    : undefined}
                type={PanelType.medium}
                headerText="Enqueue selected tasks"
            >
                <Stack tokens={{ childrenGap: 16 }}>
                {submitError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{submitError}</div>
                    </MessageBar>
                )}
                {submitNotice && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        {submitNotice}
                    </MessageBar>
                )}

                {agencyStatusError && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        {agencyStatusError}
                    </MessageBar>
                )}

                {agencyJobCreationStatus?.isPaused && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        Agency job creation is paused due to the{' '}
                        {agencyJobCreationStatus.throttleType === AgencyJobCreationThrottleType.Concurrency
                            ? 'concurrent job limit'
                            : 'daily job limit'}
                        {agencyJobCreationStatus.resumeAtUtc
                            ? ` until ${formatDate(agencyJobCreationStatus.resumeAtUtc)}`
                            : ''}
                        . You can still enqueue tasks; they will wait in the internal queue.
                    </MessageBar>
                )}

                {isStaleSnapshot && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        The selected snapshot is {snapshotAgeDays} days old. Consider refreshing it before enqueueing new work.
                    </MessageBar>
                )}

                <Text>
                    {taskIds.length} task{taskIds.length === 1 ? '' : 's'} will be enqueued against snapshot{' '}
                    {snapshot ? `#${snapshot.snapshotId}` : '-'}.
                </Text>

                <Dropdown
                    label="Job"
                    options={availableJobOptions}
                    selectedKey={selectedJobName}
                    onChange={(_, option) => {
                        setSelectedJobName(option?.key ? String(option.key) : undefined);
                        setSubmitDirectlyToAgency(false);
                        setPromptPreview(null);
                    }}
                    placeholder={loadingAgents ? 'Loading job definitions...' : (availableJobOptions.length > 0 ? 'Select a job' : 'No enabled job definitions support this task type')}
                    disabled={submitting || loadingAgents || availableJobOptions.length === 0}
                />

                {supportsModelOverride && (
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="mediumPlus">Override model</Text>
                        <Text styles={{ root: { color: '#605e5c' } }}>
                            Default sends no override and uses the selected job definition&apos;s saved model configuration.
                        </Text>
                        {agencyModelsMessage && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                {agencyModelsMessage}
                            </MessageBar>
                        )}

                        {isAgencyJob && (
                            <Stack tokens={{ childrenGap: 4 }}>
                                <Checkbox
                                    label="Submit directly to Agency"
                                    checked={submitDirectlyToAgency}
                                    onChange={(_, checked) => setSubmitDirectlyToAgency(checked ?? false)}
                                    disabled={submitting}
                                />
                                {!submitDirectlyToAgency && (
                                    <Text styles={{ root: { color: '#605e5c' } }}>
                                        The work items will be saved immediately and submitted by the observer when job concurrency permits.
                                    </Text>
                                )}
                            </Stack>
                        )}
                        <AgencyModelConfigurationEditor
                            value={overrideModelConfiguration}
                            modelOptions={agencyModelOptions}
                            disabled={submitting || agencyModelsLoading}
                            onChange={setOverrideModelConfiguration}
                        />
                    </Stack>
                )}

                {snapshot && (
                    <Text styles={{ root: { color: '#605e5c' } }}>
                        Snapshot {snapshot.name || `#${snapshot.snapshotId}`} • {formatCompactDate(snapshot.date)}
                    </Text>
                )}

                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        text={submitting ? 'Enqueueing...' : 'Enqueue tasks'}
                        onClick={handleSubmit}
                        disabled={submitting || partialFailureAfterSubmit || submissionCompleted || loadingAgents || availableJobOptions.length === 0 || !snapshot || taskIds.length === 0}
                    />
                    <DefaultButton
                        text={previewLoading ? 'Loading preview...' : 'Preview first task prompt'}
                        onClick={handlePreview}
                        disabled={submitting || previewLoading || loadingAgents || !selectedJobName || !snapshot || taskIds.length === 0}
                    />
                    <DefaultButton text={partialFailureAfterSubmit || submissionCompleted ? 'Close' : 'Cancel'} onClick={handlePanelDismiss} disabled={submitting} />
                </Stack>
                </Stack>
            </Panel>

            <Dialog
                hidden={!promptPreview}
                onDismiss={dismissPromptPreview}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Prompt preview',
                    subText: promptPreview
                        ? `Task ${promptPreview.taskId}: ${promptPreview.taskScope}`
                        : undefined,
                    topButtonsProps: [{
                        ariaLabel: 'Close prompt preview',
                        iconProps: { iconName: 'Cancel' },
                        onClick: dismissPromptPreview,
                    }],
                }}
                modalProps={{ isBlocking: false }}
                minWidth={720}
                maxWidth={960}
            >
                {promptPreview?.containsSasPlaceholders && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        SAS placeholders remain unexpanded in this preview. Fresh URLs are generated when the job is dispatched.
                    </MessageBar>
                )}
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text><strong>Job:</strong> {promptPreview?.jobName}</Text>
                    <Text><strong>Snapshot:</strong> {promptPreview?.snapshotId}</Text>
                </Stack>
                <TextField
                    label="Stable resolved prompt"
                    value={promptPreview?.prompt ?? ''}
                    multiline
                    rows={20}
                    readOnly
                />
                <DialogFooter>
                    <PrimaryButton text="Close" onClick={dismissPromptPreview} />
                </DialogFooter>
            </Dialog>
        </>
    );
}
